/*
 * 即時搜尋面板 (header 放大鏡 + /search 頁嘅搜尋格)
 *
 *   撳開搜尋 ──→ 攞索引 (localStorage 快取 30 分鐘;冇就 fetch 一次,產品按銷量排)
 *                 │
 *        ┌────────┴──────────────────────────┐
 *        ↓ 未打字                              ↓ 打緊字 (同義字展開 + 計分)
 *   左:熱門搜尋 chips (撳落即搜)          左:對到嘅系列 chips + 「睇晒全部結果」
 *   右:熱門產品 (銷量頭 4 件)             右:產品大圖格 (頭 8 件,有結果數)
 *                                          │
 *                                          ↓ 乜都對唔到
 *                                   「搵唔到」+ WhatsApp 問我哋 + 熱門搜尋 / 熱門產品
 *
 * 點解唔用 Shopify /search/suggest:店舖語言 zh-TW,佢一律回 417 "Unsupported buyer locale"。
 */
const PS_INDEX_URL = '/collections/all?view=search-index&sort_by=best-selling';
const PS_CACHE_KEY = 'im-search-index-v2';
const PS_CACHE_TTL = 30 * 60 * 1000;
const PS_MAX_PRODUCTS = 8;
const PS_POPULAR_PRODUCTS = 4;
const PS_MAX_COLLECTIONS = 8;

// 每組入面嘅字當係同一樣嘢;搵其中一個會一齊搵埋其他
const PS_SYNONYMS = [
  ['梳化', '沙發', 'sofa', 'couch'],
  ['梳化床', '沙發床', 'sofabed', 'sofa bed', 'daybed'],
  ['床褥', '床墊', 'mattress'],
  ['餐檯', '飯檯', 'dining table', 'dining'],
  ['電動', '電鉸', 'recliner', 'electric'],
  ['寵物', '貓', '狗', '貓爪', 'pet', 'cat', 'dog'],
  ['地櫃', '電視櫃', 'console', 'tv'],
  ['真皮', '皮', 'leather'],
  ['布藝', '布', 'fabric'],
  ['貴妃', 'chaise'],
  ['儲物', '收納', 'storage'],
  ['零靠牆', '貼牆', '零距離', '零牆距', 'wall hugger'],
  ['慳位', '細單位', '細廳', '細位', '纖薄', 'small space'],
  // 座位數:Rybbit 2026-09 有人搵「單人」「2人」「4座梳化」搵唔到嘢;索引有埋選項值 (132cm 2背…) 先對得到
  ['單人', '一人', '1人', '單座', '1座', '1背', '單人位'],
  ['2人', '兩人', '二人', '2座', '兩座', '雙座', '2背'],
  ['3人', '三人', '3座', '三座', '3背'],
  ['4人', '四人', '4座', '四座', '4背']
];

function psNormalize(text) {
  return (text || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[枱臺]/g, '檯')
    .replace(/牀/g, '床')
    .replace(/[|｜、，,。．.!！?？()（）「」【】"“”:：;；\-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const PS_SYNONYM_GROUPS = PS_SYNONYMS.map((group) => group.map(psNormalize));

function psVariants(token) {
  const variants = new Set([token]);
  PS_SYNONYM_GROUPS.forEach((group) => {
    if (group.includes(token)) group.forEach((word) => variants.add(word));
  });
  return [...variants];
}

function psEscape(text) {
  return String(text).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);
}

let psIndexPromise = null;

function psLoadIndex() {
  if (psIndexPromise) return psIndexPromise;

  try {
    const cached = JSON.parse(localStorage.getItem(PS_CACHE_KEY));
    if (cached && Date.now() - cached.at < PS_CACHE_TTL) {
      psIndexPromise = Promise.resolve(psPrepareIndex(cached.data));
      return psIndexPromise;
    }
  } catch (e) {}

  psIndexPromise = fetch(PS_INDEX_URL, { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    })
    .then((data) => {
      try {
        localStorage.setItem(PS_CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
      } catch (e) {}
      return psPrepareIndex(data);
    })
    .catch((error) => {
      psIndexPromise = null;
      throw error;
    });

  return psIndexPromise;
}

function psPrepareIndex(data) {
  return {
    products: psPrepare(data.products || []),
    collections: psPrepare(data.collections || [])
  };
}

function psPrepare(items) {
  return items.map((item) => {
    const title = psNormalize(item.t);
    const keywords = psNormalize(item.k || '');
    const handle = psNormalize(item.h);
    return {
      ...item,
      title,
      titleCompact: title.replace(/ /g, ''),
      keywords,
      handle
    };
  });
}

// 一個字喺一件產品度嘅分數;0 = 對唔到
function psScoreToken(product, token) {
  let best = 0;
  psVariants(token).forEach((word) => {
    const exact = word === token;
    const weight = exact ? 1 : 0.8;
    let score = 0;
    if (product.titleCompact.startsWith(word.replace(/ /g, ''))) score = 12;
    else if (product.title.includes(word)) score = 8;
    else if (product.keywords.includes(word)) score = 5;
    else if (product.handle.includes(word)) score = 3;
    best = Math.max(best, score * weight);
  });
  if (best) return best;

  // 中文字連埋打 (例如「電動梳化」),成串對唔到就逐兩個字對,對到一半以上當部分命中
  if (/[㐀-鿿]/.test(token) && token.length >= 3) {
    const haystack = `${product.title} ${product.keywords}`;
    let hits = 0;
    let total = 0;
    for (let i = 0; i < token.length - 1; i++) {
      total++;
      const pair = token.slice(i, i + 2);
      if (psVariants(pair).some((word) => haystack.includes(word))) hits++;
    }
    if (hits / total > 0.5) return 4 * (hits / total);
  }
  return 0;
}

// 型號打咗數字 (Rybbit 2026-09:「1401」「1301」「404」搵唔到 I401 / I301 / I404):
// 「1401」/「l401」→「i401」、「401」→「i401」;淨係真係有呢個型號先換,唔係就照用客打嘅字
function psModelToken(index, token) {
  const match = /^[1il]?([34]\d{2})$/.exec(token);
  if (!match) return token;
  const model = `i${match[1]}`;
  return index.some((item) => item.titleCompact.startsWith(model) || item.handle.startsWith(model)) ? model : token;
}

// 回傳 items 按相關度排好;index 本身係銷量次序,分數一樣就賣得好嘅行先
function psSearch(index, query) {
  const normalized = psNormalize(query);
  if (!normalized) return [];

  // 成句本身係同義字 (例如 "sofa bed") 就唔好拆開
  const tokens = (PS_SYNONYM_GROUPS.some((group) => group.includes(normalized))
    ? [normalized]
    : normalized.split(' ')).map((token) => psModelToken(index, token));

  const results = [];
  index.forEach((product, position) => {
    let score = 0;
    for (const token of tokens) {
      const tokenScore = psScoreToken(product, token);
      if (!tokenScore) return;
      score += tokenScore;
    }
    if (product.a === false) score -= 2;
    results.push({ product, score, position });
  });

  return results
    .sort((a, b) => b.score - a.score || a.position - b.position)
    .map((result) => result.product);
}


function psSplitTitle(title) {
  const parts = String(title).split(/\s*[|｜]\s*/);
  return { name: parts.shift(), sub: parts.filter(Boolean).join(' · ') };
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.strings = window.predictiveSearchStrings || {};

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 60).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // 熱門搜尋 chip:填入搜尋格即刻搜,唔使轉頁
    this.predictiveSearchResults.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-ps-term]');
      if (!chip) return;
      event.preventDefault();
      this.input.value = chip.getAttribute('data-ps-term');
      this.input.focus();
      this.onChange();
    });

    // 一打開搜尋 modal 就預先攞索引,第一個字已經即時有結果
    const details = this.closest('details');
    if (details) details.addEventListener('toggle', () => { if (details.open) psLoadIndex().catch(() => {}); });
    this.input.addEventListener('pointerdown', () => psLoadIndex().catch(() => {}), { once: true });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.getSearchResults(this.getQuery());
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[data-ps-option][aria-selected="true"]')) event.preventDefault();
  }

  onFocus() {
    if (this.getAttribute('results') === 'true' && this.renderedQuery === this.getQuery()) {
      this.open();
    } else {
      this.getSearchResults(this.getQuery());
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  // 上下掣:先行產品卡,再行 chips 同「睇晒全部結果」
  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const all = [...this.querySelectorAll('[data-ps-option]')];
    const options = all.filter((el) => el.closest('.ps-grid')).concat(all.filter((el) => !el.closest('.ps-grid')));
    if (!options.length) return;

    const selected = this.querySelector('[data-ps-option][aria-selected="true"]');
    let position = options.indexOf(selected);

    if (direction === 'up') {
      if (!selected) return;
      position = position <= 0 ? options.length - 1 : position - 1;
    } else {
      position = position === -1 || position === options.length - 1 ? 0 : position + 1;
    }

    const active = options[position];
    if (selected) selected.setAttribute('aria-selected', false);
    active.setAttribute('aria-selected', true);
    active.scrollIntoView({ block: 'nearest' });

    this.setLiveRegionText(active.textContent.replace(/\s+/g, ' ').trim());
    this.input.setAttribute('aria-activedescendant', active.id);
  }

  selectOption() {
    const selected = this.querySelector('[data-ps-option][aria-selected="true"]');

    if (selected) selected.click();
  }

  getSearchResults(searchTerm) {
    if (!this.isConnected) return;
    this.setLiveRegionLoadingState();

    psLoadIndex()
      .then((index) => {
        // 等索引期間客人可能打多咗字,只顯示最新嗰次
        if (searchTerm !== this.getQuery()) return;
        this.renderedQuery = searchTerm;
        this.renderSearchResults(this.buildResultsMarkup(index, searchTerm));
      })
      .catch(() => {
        // 索引攞唔到就收起面板,Enter 照樣去 /search 結果頁
        this.removeAttribute('loading');
        this.close();
      });
  }

  buildChips(items, heading, countText) {
    if (!items.length) return '';
    return `
      <div class="ps-panel__block">
        <h2 class="ps-panel__title"><span>${psEscape(heading)}</span>${countText ? `<span class="ps-panel__count">${psEscape(countText)}</span>` : ''}</h2>
        <div class="ps-chips">${items.join('')}</div>
      </div>`;
  }

  buildProductGrid(products, heading, countText) {
    const s = this.strings;
    const cards = products.map((product, i) => {
      const title = psSplitTitle(product.t);
      const price = s.showPrice && product.p
        ? `<span class="ps-card__price">${psEscape(product.p)}${product.c ? ` <s>${psEscape(product.c)}</s>` : ''}</span>`
        : '';
      return `
        <li class="ps-grid__item">
          <a href="${psEscape(product.u)}" id="ps-option-product-${i}" class="ps-card" data-ps-option role="option" aria-selected="false" tabindex="-1">
            <span class="ps-card__media">
              ${product.i ? `<img src="${psEscape(product.i)}" alt="" width="400" height="400" loading="lazy">` : ''}
              ${product.a === false ? `<span class="ps-card__badge">${psEscape(s.soldOut || '')}</span>` : ''}
            </span>
            <span class="ps-card__name">${psEscape(title.name)}</span>
            ${title.sub ? `<span class="ps-card__sub">${psEscape(title.sub)}</span>` : ''}
            ${price}
          </a>
        </li>`;
    }).join('');

    return `
      <div class="ps-panel__main">
        <h2 class="ps-panel__title"><span>${psEscape(heading)}</span>${countText ? `<span class="ps-panel__count">${psEscape(countText)}</span>` : ''}</h2>
        <ul id="predictive-search-results-list" class="ps-grid list-unstyled" role="listbox" aria-label="${psEscape(heading)}">${cards}</ul>
      </div>`;
  }

  popularChips() {
    return (this.strings.popularTerms || []).map((term, i) =>
      `<button type="button" id="ps-option-term-${i}" class="ps-chip" data-ps-option data-ps-term="${psEscape(term)}" role="option" aria-selected="false" tabindex="-1">${psEscape(term)}</button>`);
  }

  buildResultsMarkup(index, searchTerm) {
    const s = this.strings;
    const count = (n) => (s.resultsCount || '[count]').replace('[count]', n);
    const popularProducts = index.products.slice(0, PS_POPULAR_PRODUCTS);
    let aside;
    let main;
    let cta = '';
    let liveText;

    if (!searchTerm) {
      aside = this.buildChips(this.popularChips(), s.popularSearches || '');
      main = this.buildProductGrid(popularProducts, s.popularProducts || '');
      liveText = s.popularProducts || '';
    } else {
      const products = psSearch(index.products, searchTerm);
      const collections = psSearch(index.collections, searchTerm);

      if (!products.length && !collections.length) {
        const waText = (s.whatsappText || '').replace('[terms]', searchTerm);
        aside = `
          <div class="ps-panel__block ps-panel__empty">
            <p class="ps-panel__empty-text">${psEscape((s.noResults || '').replace('[terms]', searchTerm))}</p>
            <a href="https://wa.me/${psEscape(s.whatsappNumber || '')}?text=${encodeURIComponent(waText)}" id="ps-option-whatsapp" class="button button--secondary ps-panel__cta" data-ps-option aria-selected="false" target="_blank" rel="noopener" tabindex="-1">${psEscape(s.whatsappCta || '')}</a>
          </div>
          ${this.buildChips(this.popularChips(), s.popularSearches || '')}`;
        main = this.buildProductGrid(popularProducts, s.popularProducts || '');
        liveText = (s.noResults || '').replace('[terms]', searchTerm);
      } else {
        const collectionChips = collections.slice(0, PS_MAX_COLLECTIONS).map((collection, i) =>
          `<a href="${psEscape(collection.u)}" id="ps-option-collection-${i}" class="ps-chip" data-ps-option role="option" aria-selected="false" tabindex="-1">${psEscape(collection.t)}</a>`);
        aside = collections.length
          ? this.buildChips(collectionChips, s.collections || '', count(collections.length))
          : this.buildChips(this.popularChips(), s.popularSearches || '');
        cta = `<div class="ps-panel__cta-wrap"><button type="submit" id="ps-option-view-all" class="button ps-panel__cta" data-ps-option aria-selected="false" tabindex="-1">${psEscape(s.viewAll || '')}</button></div>`;
        main = products.length
          ? this.buildProductGrid(products.slice(0, PS_MAX_PRODUCTS), s.products || '', count(products.length))
          : this.buildProductGrid(popularProducts, s.popularProducts || '');
        liveText = count(products.length);
      }
    }

    return `
      <div class="ps-panel" id="predictive-search-results">
        <div class="ps-panel__aside">${aside}</div>
        ${main}
        ${cta}
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${psEscape(liveText)}</span>`;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.input.setAttribute('aria-activedescendant', '');
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - this.predictiveSearchResults.getBoundingClientRect().top;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute('open', true);
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.input.setAttribute('aria-expanded', true);
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

if (typeof customElements !== 'undefined') customElements.define('predictive-search', PredictiveSearch);
